import {
  DocumentNode,
  LazyQueryHookOptions,
  OperationVariables,
  TypedDocumentNode,
  useLazyQuery,
} from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';

const DEFAULT_POLL_TIMEOUT_MS = 60000;
const DEFAULT_POLL_INTERVAL_MS = 5000;

export interface QueryLongPollHookOptions<TData, TVariables> extends LazyQueryHookOptions<TData, TVariables> {
  /**
   * The maximum time to poll
   * @default 60 seconds
   */
  pollTimeout?: number;
}

export interface QueryLongPollReturn<TData> {
  startLongPoll: () => void;
  endLongPoll: () => void;
  isPolling: boolean;
  data: TData | undefined;
}

export const useQueryLongPoll = <TData = Record<string, unknown>, TVariables = OperationVariables>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options: QueryLongPollHookOptions<TData, TVariables>,
): QueryLongPollReturn<TData> => {
  const pollInterval = options?.pollInterval ?? DEFAULT_POLL_INTERVAL_MS;
  const pollTimeout = options?.pollTimeout ?? DEFAULT_POLL_TIMEOUT_MS;

  const [isPolling, setIsPolling] = useState(false);

  const [lazyQuery, { data, stopPolling }] = useLazyQuery<TData, TVariables>(query, {
    pollInterval,
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
    ...options,
  });

  const startLongPoll = useCallback(() => {
    setIsPolling(true);
    lazyQuery();
  }, [lazyQuery]);

  const endLongPoll = useCallback(() => {
    setIsPolling(false);
    stopPolling?.();
  }, [stopPolling]);

  const startTimeoutCountdown = useCallback(() => {
    setTimeout(() => {
      endLongPoll();
    }, pollTimeout);
  }, [endLongPoll, pollTimeout]);

  useEffect(() => {
    isPolling && startTimeoutCountdown();
  }, [isPolling, startTimeoutCountdown]);

  return {
    startLongPoll,
    endLongPoll,
    isPolling,
    data,
  };
};
